import {
  Badge,
  InputSelectionList,
  InputSelectionListChangeEvent,
  InputSelectionListProps,
} from '@dixa/lib-shared-habla';
import { ConversationLanguageCode } from 'lib-shared-languages';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DeepWritable } from 'ts-essentials';

import { ConversationContext } from 'components/conversation';
import {
  LanguageSelectionListValue,
  useLanguageSelectionList,
  UseLanguageSelectionListOptions,
} from 'utils/useLanguageSelectionList';
import {
  LanguageSelectionListItemContent,
  useLanguageSelectionListItems,
} from 'utils/useLanguageSelectionListItems';
import { useServerStorage } from 'utils/useServerStorage';

export type CopilotTranslateOnLanguageChange = (event: {
  selectedLanguage: LanguageSelectionListValue;
}) => void;

type CopilotLanguageInputSelectionListProps = {
  /** The selected language code. */
  selectedLanguageCode?: ConversationLanguageCode;
  /** The callback for when the language selection changes. */
  onLanguageChange?: CopilotTranslateOnLanguageChange;
} & Omit<
  InputSelectionListProps<LanguageSelectionListValue>,
  'getItemTitleByValue'
>;

/**
 * Represents a selection list of languages for translating text to a selected
 * language.
 */
export const CopilotLanguageInputSelectionList = (
  props: CopilotLanguageInputSelectionListProps,
): JSX.Element => {
  const { selectedLanguageCode, onLanguageChange, ...inputSelectionListProps } =
    props;

  const [searchTerm, setSearchTerm] = useState('');
  const { conversation } = useContext(ConversationContext);
  const conversationLanguageCode =
    conversation?.attributes && 'language' in conversation.attributes
      ? conversation.attributes.language
      : undefined;
  const { data, set } = useServerStorage('user');

  const recentlyUsedLanguagesCodes = useMemo(
    () => data.agent_copilot?.recentlyUsedLanguageCodesReplyMessage ?? [],
    [data.agent_copilot],
  );

  const languageSelectionListOptions = useMemo<UseLanguageSelectionListOptions>(
    () => ({
      conversationLanguageCode,
      selectedLanguageCodes: selectedLanguageCode
        ? [selectedLanguageCode]
        : undefined,
    }),
    [conversationLanguageCode, selectedLanguageCode],
  );

  const { languages, selectedLanguages, onChange } = useLanguageSelectionList(
    languageSelectionListOptions,
  );

  const languageSelectionListItems = useLanguageSelectionListItems({
    languages,
    searchTerm,
    recentlyUsedLanguagesCodes,
    sortFn: (a, b) =>
      b.code === conversationLanguageCode ? 1 : a.name.localeCompare(b.name),
    itemContentFn: (language) => (
      <LanguageSelectionListItemContent
        language={language}
        endSlot={
          language.code === conversationLanguageCode ? (
            <Badge size="small" variant="secondary">
              Conversation language
            </Badge>
          ) : undefined
        }
      />
    ),
  });

  const handleOnChange = useCallback<typeof onChange>(
    (event: InputSelectionListChangeEvent<LanguageSelectionListValue>) => {
      onChange(event);

      set({
        agent_copilot: (draft) => {
          draft.recentlyUsedLanguageCodesReplyMessage = Array.from(
            new Set([
              event.selectedItem?.value.code ?? null,
              ...recentlyUsedLanguagesCodes,
            ]),
          ).slice(0, 5);
        },
      });

      if (event.selectedItem?.value) {
        onLanguageChange?.({ selectedLanguage: event.selectedItem?.value });
      }
    },
    [onChange, onLanguageChange, recentlyUsedLanguagesCodes, set],
  );

  return (
    <InputSelectionList<LanguageSelectionListValue>
      {...inputSelectionListProps}
      getItemTitleByValue={(value) => value?.name}
      value={selectedLanguages as DeepWritable<typeof selectedLanguages>}
      onChange={handleOnChange}
      placeholder="Select a language"
      filter={{
        value: searchTerm,
        onChange: (event) => setSearchTerm(event.target.value),
      }}
      dropdownProps={{
        style: { maxHeight: 418, overflow: 'auto' },
      }}
      style={{ maxWidth: 320 }}
    >
      {languageSelectionListItems}
    </InputSelectionList>
  );
};
